/*
 * Authors: mariushirjanu, Triadi, Koen
 * Created: 01/06/2016
 * Upgraded: 01/06/2023
 */
import _ from 'underscore';
import { AfterViewInit, Component, Input, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { WIZARD_ORDER } from '@app/shared/app-wizards';
import { IPricesResponse, PriceCartService } from '../../cart/price-cart.service';
import { AuthService } from '@app/shared/services/auth/auth.service';
import { FormattingService } from '@app/shared/services/formatting.service';
import { OrderEntryChargesService } from './order-entry-charges.service';
import { IOrderCharge } from '../order-entry-data.service';
import { OrderEntryWebshopService } from '../webshop/order-entry-webshop.service';
import { GaWizardService } from '@app/shared/wizard/ga-wizard.service';
import { GaValidateService, IValidatingComponent } from '@app/shared/formly/ga-validate.service';
import { TranslationService } from '@app/shared/translation/translation.service';
import { GaMessageService } from '@app/shared/message/ga-message.service';
import { FormlyInputType, FormlyType, GaFormlyService, IExtraFieldConfigurationObject } from '@app/shared/formly/ga-formly.service';
import { GaWizard } from '@app/shared/wizard/ga-wizard';
import { UntypedFormGroup } from '@angular/forms';
import { GaEventService, IGAEvent } from '@app/shared/services/ga-event.service';
import { EVENT_ORDER_CHARGES_CHANGED, EVENT_ORDER_TAX_PERCENTAGE_CHANGED } from '@app/shared/app-events';
import { Subscription } from 'rxjs';
import { IUserData } from '@app/shared/services/auth/auth-data.service';
import { GridOptions, ICellRendererParams, ValueFormatterParams } from 'ag-grid-community';
import { IFormGridColDef } from '@app/shared/form-grid/form-grid.component';
import { LovService } from '@app/shared/services/lov.service';
import { OrderEntryChargesDataService } from './order-entry-charges-data.service';
import { GaDeferredService, IGADeferred } from '@app/shared/services/ga-deferred.service';
import { OrderEntryStepId } from '../order-entry-steps';

import { GRID_ORDER_ENTRY_CHARGES } from '@app/shared/app-grids';

interface IChargesModel {
  taxPercentage:number;
}

interface IChargesGrid {
  rows?:IOrderCharge[];
  columns:IFormGridColDef[];
}

@Component({
  selector: 'order-entry-charges',
  template: `
    <h2>{{ title }}</h2>

    <div class="order-charges">
      <formly [form]="form" [fields]="fields" [model]="model" class="charges-vat form-horizontal"></formly>

      <form-grid [title]="title"
        gridId="${GRID_ORDER_ENTRY_CHARGES}"
        [loading]="loadingCharges"
        [gridOptions]="gridOptions"
        [rowData]="rowData"
        [allowAdd]="allowEditCharges"
        [allowEdit]="allowEditCharges"
        [allowDelete]="allowEditCharges"
        [showActionButtons]="true"
        noResultsText="ORDERENTRY.CHARGES.NO_CHARGES"
        (onRowEditing)="onRowEditing($event)"
        (onChange)="onModelChange($event)"
      >
        <button *ngIf="allowRecalculateCharges" class="btn btn-warning charges-recalculate" (click)="recalculate()" [disabled]="loadingCharges || aRowIsEditing">
          <i class="fa fa-refresh"></i>&nbsp;<span translate>ORDERENTRY.CHARGES.RECALCULATE</span>
        </button>
      </form-grid>
    </div>
  `,
  styleUrls: [ 'charges.scss' ],
  encapsulation: ViewEncapsulation.None
})
export class OrderEntryChargesComponent implements IValidatingComponent, OnInit, AfterViewInit, OnDestroy {
  @Input() public orderId:number;

  public form:UntypedFormGroup = new UntypedFormGroup({});
  public title:string;
  public model:IChargesModel;
  public grid:IChargesGrid;
  public allowRecalculateCharges:boolean;
  public fields:IExtraFieldConfigurationObject[];
  public loadingCharges:boolean;
  public rowData:IOrderCharge[];
  public allowEditCharges:boolean;

  private wizard:GaWizard;
  private loadingVat:boolean;
  private allowEditVAT:boolean;
  private editableOwnerTypes:string[];
  private eventHandler:Subscription;
  public gridOptions:GridOptions;
  public aRowIsEditing:boolean;
  private onValidationReadyDeferred:IGADeferred<void>;

  constructor(
    private authService:AuthService,
    private formattingService:FormattingService,
    private formlyService:GaFormlyService,
    private eventService:GaEventService,
    private messageService:GaMessageService,
    private orderEntryChargesService:OrderEntryChargesService,
    private priceCartService:PriceCartService,
    private translationService:TranslationService,
    private validateService:GaValidateService,
    private lovService:LovService,
    private orderEntryWebshopService:OrderEntryWebshopService,
    private orderEntryChargesDataService:OrderEntryChargesDataService,
    private wizardService:GaWizardService,
    deferredService:GaDeferredService
  ) {
    this.wizard = this.wizardService.getInstance(WIZARD_ORDER);
    this.onValidationReadyDeferred = deferredService.defer();
  }

  public ngOnInit():void {
    this.start();
    this.listen();
  }

  public ngAfterViewInit():void {
    this.onValidationReadyDeferred.resolve();
  }

  public start():void {
    this.title = this.translationService.instant('ORDERENTRY.CHARGES.TITLE');

    if(this.orderEntryWebshopService.shopContext) {
      this.model = { taxPercentage: this.orderEntryChargesService.taxPercentage };
      this.allowEditVAT = this.orderEntryWebshopService.shopContext.orderPermissions.allowApplyVAT;
      this.editableOwnerTypes = this.orderEntryWebshopService.shopContext.orderPermissions.editableOwnerTypes;
      this.allowRecalculateCharges = this.orderEntryWebshopService.shopContext.orderPermissions.allowRecalculateCharges;

      this.setGridPermissions();

      this.fields = this.createFields();

      this.gridOptions = {
        columnDefs: this.columnDefs,
        rowData: this.orderEntryChargesService.getCharges() || [],
        context: {
          preventExitEditing: true // prevent switching to edit mode for another row when a row is currently being edited
        }
      };

      this.wizard.currentValidator(this.validate, true, this.beforeValidate, OrderEntryStepId.CHARGES);

      this.loadCharges();
    }
  }

  private listen():void {
    // update service when the model changes
    this.form.valueChanges.subscribe((model:IChargesModel) => {
      this.orderEntryChargesService.taxPercentage = model.taxPercentage;
    });

    // update form model when tax percentage changes
    this.eventHandler = this.eventService.events.subscribe((event:IGAEvent) => {
      if (event.name === EVENT_ORDER_TAX_PERCENTAGE_CHANGED && this.model.taxPercentage !== event.data) {
        this.form.patchValue({ taxPercentage: event.data });
      }

      if(event.name === EVENT_ORDER_CHARGES_CHANGED){
        this.rowData = event.data;
      }
    });
  }

  private get columnDefs():IFormGridColDef[] {
    const checkboxRenderer:Function = (value:boolean) => value? '<i class="fa fa-check successText"></i>' : '';

    return [ {
      field: 'chargeType',
      headerName: 'ORDERENTRY.CHARGES.TYPE',
      valueFormatter: (params:ValueFormatterParams) => this.formattingService.formatLov(params.value),
      flex: 2,
      cellEditorParams: {
        type: FormlyType.SELECT,
        editableOnAdd: true,
        required: true,
        optionsFn: this.orderEntryChargesDataService.chargeTypes,
        format: this.formattingService.formatLov
      }
    }, {
      field: 'value',
      headerName: 'ORDERENTRY.CHARGES.AMOUNT',
      valueFormatter: this.formatAmountWithCurrency,
      type: 'rightAligned',
      flex: 1,
      editable: true,
      cellEditorParams: {
        type: FormlyType.INPUT,
        inputType: FormlyInputType.NUMBER,
        addOn: this.priceCartService.currency,
        required: true
      }
    }, {
      field: 'ownerType',
      headerName: 'ORDERENTRY.CHARGES.OWNER',
      valueFormatter: (params:ValueFormatterParams) => this.formattingService.formatLov(params.value),
      editable: true,
      cellEditorParams: {
        type: FormlyType.SELECT,
        format: this.formattingService.formatLov,
        required: true,
        optionsFn: () => this.lovService.list('DOMRGTY'),
        defaultValue: {
          id: 'BP',
          value: 'BP',
          sequence: 10
        }
      }
    }, {
      field: 'manual',
      headerName: 'ORDERENTRY.CHARGES.MANUAL',
      cellStyle: {
        textAlign: 'center'
      },
      cellRenderer: (params:ICellRendererParams) => {
        return checkboxRenderer(params.value);
      },
      cellEditorParams: {
        type: FormlyType.CHECKBOX,
        editableOnUpdate: true,
        defaultValue: true
      },
      minWidth: 50,
      maxWidth: 120
    }];
  }

  private createFields():IExtraFieldConfigurationObject[] {
    return [
      this.formlyService.aField(this.allowEditVAT ? FormlyType.INPUT : FormlyType.VALUE, 'taxPercentage', 'ORDERENTRY.CHARGES.VAT_LABEL')
        .isRequired()
        .withWrapperClass('col-sm-6')
        .withLabelClass('col-sm-8')
        .withValueClass('col-sm-4')
        .withAddOn(this.allowEditVAT ? '%' : '')
        .withDisabled(this.loadingVat)
        .withInputType('number')
        .build()
    ];
  }

  public recalculate():void {
    this.loadCharges();
  }

  public beforeValidate = ():Promise<any> => {
    return this.onValidationReadyDeferred.promise;
  };

  public validate = ():Promise<string[]|void> => {
    const extraErrors:string[] = [];
    if (this.aRowIsEditing) {
      extraErrors.push(this.translationService.instant('ORDERENTRY.CHARGES.CONFIRM_CHARGES'));
    }

    return this.validateService.validateFields(this.fields, this.model, true, extraErrors);
  };

  private setGridPermissions():void {
    const currentUser:IUserData = this.authService.getCurrentUser();
    this.allowEditCharges = _.contains(this.editableOwnerTypes, currentUser.userGroup.name);
  }

  private loadCharges():void {
    this.loadingCharges = true;
    const statusMessage:Noty = this.messageService.showStatusMessage('Recalculating Charges');

    this.priceCartService.load(this.orderId, true)
      .then((prices:IPricesResponse) => {
        this.orderEntryChargesService.setCharges(prices ? (<IPricesResponse>prices).purchasePriceTotals?.charges : [], this.editableOwnerTypes);
      })
      .finally(() => {
        this.messageService.closeMessage(statusMessage);
        this.loadingCharges = false;
      });
  }

  private formatAmountWithCurrency = (params:ValueFormatterParams) => {
    return this.formattingService.formatAmount(params.value, this.priceCartService.currency);
  };

  public onModelChange(model:IOrderCharge[]):void {
    this.orderEntryChargesService.setCharges(model, this.editableOwnerTypes);
  }

  public onRowEditing(aRowIsEditing:boolean):void {
    this.aRowIsEditing = aRowIsEditing;
  }

  public ngOnDestroy():void {
    this.eventHandler.unsubscribe();
    this.wizard.clearCurrentValidator();
  }
}